import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ArtworkDBType, ArtworkStatusEnum, RareFlag } from '~/graphqls/types'
import { FenToYuan } from '~/utils'
import { MyImage } from '~/components/ui/MyImage'
import Link from 'next/link'
import Share from '../icon/Share'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'
import { MyRare } from '../ui/MyRare'
import classNames from 'classnames'
import CircleAndSquare from '~/components/icon/CircleAndSquare'
import { VipIcon } from '~/components/icon/Vip'
import { AiRankIcon } from '~/components/icon/AiRank'
import TagAICreate from '~/components/icon/TagAICreate'
import { getProductPrice } from '~/components/pages/product/common'
import { product } from '~/configs/mock/product'

const StyledSingleProduct = styled.div<{
  large: boolean
}>`
  margin-bottom: 32px;
  .product-thumb-wrap {
    position: relative;
    height: ${props => (props.large ? 640 : 300)}px;
    margin-bottom: 16px;
    border-radius: 16px;
    overflow: hidden;
    display: block;
    &:hover {
      .product-thumb {
        transform: scale(1.1);
        transition: all ease-in-out 180ms;
      }
    }
  }
  .product-tag {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    display: flex;
    align-items: center;
    .product-tag-item {
      font-size: 12px;
      line-height: 12px;
      padding: 7px 8px;
      border-radius: 4px;
      color: ${props => props.theme.light3};
      background: rgba(0, 0, 0, 0.56);
      margin-right: 8px;
      display: inline-flex;
      align-items: center;
    }
    .is-sell {
      background: ${props => props.theme.primaryColor};
    }
    .is-own {
      background: ${props => props.theme.successColor};
    }
  }
  .product-thumb {
    height: 100%;
    width: 100%;
    transition: all ease-in-out 180ms;
  }
  .product-info {
    display: flex;
    align-items: end;
  }
  .product-info-body {
    flex: auto;
    align-items: flex-end;
    width: ${props => (props.large ? 400 : 200)}px;
  }
  .creator-name {
    display: block;
    font-size: 12px;
    line-height: 20px;
    color: ${props => props.theme.dark2};
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
      transition: all 0.3s ease;
      color: ${props => props.theme.primaryColor};
    }
  }

  .product-title {
    display: block;
    font-size: 14px;
    line-height: 24px;
    color: ${props => props.theme.dark2};
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    transition: all 0.3s ease;
    &:hover {
      transition: all 0.3s ease;
      color: ${props => props.theme.primaryColor};
    }
  }
  .product-price {
    font-size: 12px;
    line-height: 20px;
    color: ${props => props.theme.dark2};
  }
  .creator-avatar {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    margin-bottom: 4px;
  }
`

interface Product {
  type?: ArtworkDBType | null
  creator: {
    id: any | null
    name: string | null
    avatar: string | null
    userRoleInfo: {
      artist: boolean | null
      aiArtist: boolean | null
    } | null
  } | null
  uploader?: {
    id: any | null
  } | null
  owner?: {
    id: any | null
  } | null
  status?: ArtworkStatusEnum | null
  id: any | null
  /**
   * 是否存在关联衍生品
   */
  existItem?: boolean | null
  artworkUrl: string | null
  name: string | null
  rareFlag?: RareFlag | null
  aiArtwork?: {
    extend?: {
      competition?: {
        name: string | null
      } | null
      competitionRank?: number | null
    } | null
  } | null
  buy:
    | ({
        price: any | null
        canBought: boolean | null
      } | null)[]
    | null
  use:
    | ({
        price: any | null
        canBought: boolean | null
      } | null)[]
    | null
  adapt?:
    | ({
        price: any | null
        canBought: boolean | null
      } | null)[]
    | null
}

export const SingleProduct: React.FC<{
  data: Product
  large?: boolean
  showStatus?: boolean
  showShare?: boolean
  showRank?: boolean
  showCreator?: boolean
  showRareFlag?: boolean
  needReport?: boolean
  type?: string
  onShare?: () => void
}> = observer(
  ({
    data,
    large = false,
    showStatus = false,
    showShare = false,
    showRank = false,
    showCreator = true,
    showRareFlag = true,
    needReport = false,
    type,
    onShare,
  }) => {
    const { store } = useStores()
    const aiBuyPrice = getProductPrice(data)

    const isSelf = useMemo(() => {
      return store.userinfo?.base?.uid === data.creator?.id
    }, [store.userinfo, data])

    const isUploader = useMemo(() => {
      return store.userinfo?.base?.uid === data.uploader?.id
    }, [store.userinfo, data])

    const isOwn = useMemo(() => {
      return data.owner?.id === data.creator?.id
    }, [data])

    const isAccept = useMemo(() => {
      return data.status === ArtworkStatusEnum.ACCEPT
    }, [data])

    const jumpUrl = useMemo(() => {
      // 只有当非审核通过，且是自己创作的或自己上传的，才能进入到作品编辑页
      return !isAccept && (isSelf || isUploader)
        ? `/my/create-center/${data.id}`
        : `/product/${data.id}`
    }, [isAccept, isSelf, isUploader, data])

    const onEventReport = () => {
      if (needReport) {
        window._hmt?.push(['_trackEvent', '热门推荐', 'click', data?.name, 1])
      }
    }

    /**
     * 如果不能购买所有权
     * 显示标准授权的价格
     */
    const price = useMemo(() => {
      /** 是否是AI版权作品 */
      const isAi = data?.type === ArtworkDBType.AI_COPYRIGHT
      if (isAi) {
        return aiBuyPrice.max
      }

      const canOwner = data?.buy?.some(it => {
        return it?.canBought !== false
      })
      if (canOwner) {
        return data.buy?.[0]?.price
      }
      return Math.min(data.use?.[0]?.price, data.adapt?.[0]?.price || Infinity)
    }, [product, aiBuyPrice])

    const canBuyOwner = data?.buy?.some(it => {
      return it?.canBought !== false
    })
    const isAi = data?.type === ArtworkDBType.AI_COPYRIGHT

    return (
      <StyledSingleProduct large={large}>
        <Link href={jumpUrl}>
          <a className="product-thumb-wrap" onClick={onEventReport}>
            <div className="product-tag">
              {showStatus && (
                <span
                  className={classNames(
                    {
                      'is-own': isOwn,
                      'is-sell': !isOwn,
                    },
                    'product-tag-item'
                  )}
                >
                  {isOwn ? '拥有中' : '被购买'}
                </span>
              )}
              {!!data.existItem && (
                <span className="product-tag-item">
                  <CircleAndSquare /> <span className="ml-1">文创</span>
                </span>
              )}
            </div>

            {showShare && (
              <span
                className="w-8 h-8 rounded-full absolute top-2 right-2 z-10 text-neutral-800 flex items-center justify-center cursor-pointer"
                style={{ background: 'rgba(20, 20, 22, 0.56)' }}
                onClick={e => {
                  e.preventDefault()
                  onShare?.()
                }}
              >
                <span className="w-4 h-4">
                  <Share size={16} />
                </span>
              </span>
            )}

            {showRank && (
              <>
                {data?.aiArtwork?.extend?.competitionRank && (
                  <AiRankIcon
                    rank={data?.aiArtwork?.extend?.competitionRank}
                    className="w-12 h-12 top-1 right-1"
                    textClassName="pt-[10px]"
                  />
                )}
              </>
            )}
            {type === 'aiArtwork' && (
              <span className="absolute top-2 left-2 z-10 ">
                <TagAICreate />
              </span>
            )}

            <MyImage
              tag="v800"
              className="product-thumb"
              src={data.artworkUrl}
            />
          </a>
        </Link>
        <div className="product-info">
          <div className="product-info-body">
            {showCreator && (
              <Link href={`/user/${data.creator?.id}`}>
                <a className="creator-name">
                  <span className="min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
                    {data.creator?.name}
                  </span>
                  <VipIcon
                    userRole={data.creator?.userRoleInfo}
                    border={false}
                    className="h-[14px] flex-shrink-0  max-w-[21px]"
                  />
                </a>
              </Link>
            )}
            <Link href={jumpUrl}>
              <a className="product-title" onClick={onEventReport}>
                {data?.name}{' '}
                {showRareFlag && (
                  <MyRare
                    className="-mt-0.5"
                    rare={data.rareFlag as RareFlag}
                  />
                )}
              </a>
            </Link>
            <div className="product-price">
              ¥{FenToYuan(price)}
              {!canBuyOwner && !isAi ? ' 起' : ''}
            </div>
          </div>
          {showCreator && (
            <Link href={`/user/${data.creator?.id}`}>
              <a>
                <MyImage
                  tag="v400"
                  className="creator-avatar"
                  src={data.creator?.avatar}
                />
              </a>
            </Link>
          )}
        </div>
      </StyledSingleProduct>
    )
  }
)
